import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationService } from '../../../core/services/notifications/notification.service';
import { PecNotificationPanelComponent } from '../pec-notification-panel/pec-notification-panel.component';
import { MaterialIconComponent } from '../material-icon/material-icon.component';
import { WebSocketStatus } from '../../../core/models/pec/pec-notification.model';

/**
 * Componente campana notifiche PEC per la navbar
 *
 * Features:
 * - Icona campana animata
 * - Badge con conteggio notifiche
 * - Shake animation su nuova notifica
 * - Indicatore stato connessione
 * - Click per aprire pannello
 */
@Component({
  selector: 'app-pec-notification-bell',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    BadgeModule,
    TooltipModule,
    MaterialIconComponent,
    PecNotificationPanelComponent
  ],
  templateUrl: './pec-notification-bell.component.html',
  styleUrl: './pec-notification-bell.component.scss'
})
export class PecNotificationBellComponent implements OnInit, OnDestroy {

  // Conteggio notifiche
  notificationCount = 0;

  // Stato connessione
  isConnected = false;

  // Animazione
  isShaking = false;
  isPulsing = false;

  // Pannello notifiche (riferimento al componente figlio)
  showPanel = false;

  private destroy$ = new Subject<void>();
  private lastNotificationTime = 0;

  constructor(
    private pecNotificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initializeNotifications();
    this.subscribeToConnectionStatus();
  }

  /**
   * Inizializza l'ascolto delle notifiche
   */
  private initializeNotifications(): void {
    // Sottoscrivi alle ricevute per il conteggio
    this.pecNotificationService.getReceipts()
      .pipe(takeUntil(this.destroy$))
      .subscribe(receipts => {
        const previousCount = this.notificationCount;
        this.notificationCount = receipts.length;

        // Se è aumentato, c'è una nuova notifica
        if (this.notificationCount > previousCount) {
          this.triggerNewNotificationAnimation();
        }
      });

    // Sottoscrivi alle notifiche in arrivo per animazione immediata
    this.pecNotificationService.getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.triggerNewNotificationAnimation();
      });
  }

  /**
   * Sottoscrivi allo stato della connessione
   */
  private subscribeToConnectionStatus(): void {
    this.pecNotificationService.getConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.isConnected = status === WebSocketStatus.CONNECTED;
      });
  }

  /**
   * Trigger animazione per nuova notifica
   */
  private triggerNewNotificationAnimation(): void {
    const now = Date.now();

    // Evita animazioni multiple ravvicinate (throttle 1 secondo)
    if (now - this.lastNotificationTime < 1000) {
      return;
    }

    this.lastNotificationTime = now;

    // Shake animation
    this.isShaking = true;
    setTimeout(() => {
      this.isShaking = false;
    }, 500);

    // Pulsing animation per il badge
    this.isPulsing = true;
    setTimeout(() => {
      this.isPulsing = false;
    }, 1500);
  }

  /**
   * Toggle pannello notifiche
   */
  togglePanel(): void {
    this.showPanel = !this.showPanel;

    // Reset conteggio quando apri il pannello
    if (this.showPanel) {
      // Piccolo delay per permettere all'utente di vedere il conteggio
      setTimeout(() => {
        // Non resettiamo più il conteggio automaticamente
        // L'utente può farlo manualmente con "Cancella tutto"
      }, 300);
    }
  }

  /**
   * Ottieni il tooltip
   */
  getTooltip(): string {
    if (!this.isConnected) {
      return 'Notifiche PEC - Disconnesso';
    }

    if (this.notificationCount === 0) {
      return 'Nessuna notifica PEC';
    }

    return `${this.notificationCount} ${this.notificationCount === 1 ? 'notifica' : 'notifiche'} PEC`;
  }

  /**
   * Ottieni il colore dell'icona
   */
  getBellColor(): string {
    if (!this.isConnected) {
      return '#424242'; // Grigio scuro se disconnesso
    }

    if (this.notificationCount > 0) {
      return '#ff5722'; // Arancione acceso se ci sono notifiche (massima visibilità)
    }

    return '#2e7d32'; // Verde scuro AgriFlow se connesso ma nessuna notifica
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

